/* Spejder Sport — "Skriv mig op" → Klaviyo back-in-stock.
 *
 * Posts the sold-out drawer (snippets/spejder-notify-overlay.liquid, Klaviyo
 * branch) to Klaviyo's CLIENT-side back-in-stock endpoint:
 *
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=<site id>
 *
 * Client-side on purpose: the endpoint authenticates with the PUBLIC Site ID
 * only (the same key klaviyo.js exposes), is CORS-enabled and needs no server,
 * so the drawer keeps the designed chrome instead of being replaced by
 * Klaviyo's own popup — which would also not work at all on the two card
 * surfaces (PLP hover chips / Quick View), since Klaviyo only auto-injects its
 * button on product pages and its trigger cannot be opened programmatically.
 * A subscription made this way fires the same "Subscribed to Back in Stock"
 * metric as Klaviyo's own form, so the client's flow triggers identically.
 *
 * The drawer is rendered up to four times per page (PDP, PLP hover card,
 * Quick View drawer, upsell drawer) and card instances are PORTALED to <body>
 * on first open (see quick-add-product.js), so this listens ONCE on document
 * in the capture phase rather than binding per form: capture also means no
 * other handler can stop the event before we see it.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  // Klaviyo pins breaking changes to a dated revision; keep this explicit so a
  // future Klaviyo default cannot silently change the contract underneath us.
  var REVISION = '2026-07-15';

  var SELECTORS = {
    form: '[data-klaviyo-notify-form]',
    host: '[data-pdp-notify-host]',
    request: '[data-product-notification-heading]',
    success: '[data-pdp-notify-success]',
    error: '[data-pdp-notify-error]',
    email: '[data-pdp-notify-email]',
    submit: '[data-pdp-notify-submit]',
    internalTrigger: '[data-pdp-notify-internal-trigger]',
    membership: '[data-pdp-notify-membership]',
    marketing: '[data-pdp-notify-marketing]',
  };

  var MEMBERSHIP_ENDPOINT = '/apps/spejder/membership/signup';

  /* Copy comes from the form's data attributes, not a JS strings global (this
     theme has none) — so both messages stay in locales/da.default.json and
     translate with the rest of the drawer. */
  function message(form, name, fallback) {
    return form.getAttribute(name) || fallback;
  }

  /* Klaviyo addresses a Shopify variant by its catalog id, not the bare
     Shopify id: "$shopify:::$default:::<external id>". The external id is the
     NUMERIC variant id — accept a GID too, since a future caller writing
     data-variant-id from the Storefront API would hand us one. */
  function catalogVariantId(raw) {
    if (!raw) return '';
    var id = String(raw).trim();
    var slash = id.lastIndexOf('/');
    if (slash !== -1) id = id.slice(slash + 1);
    if (!/^\d+$/.test(id)) return '';
    return '$shopify:::$default:::' + id;
  }

  function showError(form, text) {
    var host = form.closest(SELECTORS.host);
    var errorEl = form.querySelector(SELECTORS.error);
    if (host) host.classList.remove('has-success');
    if (!errorEl) return;
    errorEl.textContent = text;
    errorEl.hidden = false;
  }

  function clearError(form) {
    var errorEl = form.querySelector(SELECTORS.error);
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  /* Mirrors what product-notification.js does to the contact-form drawer, so
     the success state looks the same in both branches: the host's .has-success
     collapses the field/submit/consent rows (assets/spejder.css) and the
     request block is hidden outright. */
  function showSuccess(form) {
    var host = form.closest(SELECTORS.host);
    var requestEl = form.querySelector(SELECTORS.request);
    var successEl = form.querySelector(SELECTORS.success);
    clearError(form);
    if (host) host.classList.add('has-success');
    if (requestEl) requestEl.classList.add('hidden');
    if (successEl) successEl.hidden = false;
  }

  function setBusy(form, busy) {
    var submit = form.querySelector(SELECTORS.submit);
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* Runs only AFTER the Klaviyo subscription is committed. The back-in-stock
     alert is what the visitor came for; membership is a bonus, so a failure
     here must never cost them the alert or the "Tak" they can already see.
     Mirrors the member modal: enrol, then write the same cart attributes the
     checkout extension and the orders/create webhook read.

     Marketing consent goes to the app and is written straight away when the
     box was ticked (Magnus 2026-09-07, final: they accepted it, so it
     applies). It also rides the cart attribute, so an order re-applies the
     same consent idempotently. The accepted risk is documented in
     app/routes/proxy.membership.signup.tsx.

     No page reload, unlike the modal: member prices are server-rendered, so
     they appear on the visitor's next navigation rather than immediately.
     Reloading the PDP out from under someone who just asked to be notified
     about a sold-out size would cost more than it gains. */
  function joinKundeklub(email, marketingConsent) {
    fetch(MEMBERSHIP_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, termsAccepted: true, marketingConsent: !!marketingConsent }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('membership signup rejected (' + res.status + ')');
      })
      .catch(function (err) {
        // The orders/create webhook still enrols them at order completion.
        console.error('[kundeklub] drawer signup failed', err);
      })
      .then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              membership_signup: 'yes',
              membership_terms_accepted: 'yes',
              membership_marketing_consent: marketingConsent ? 'yes' : 'no',
              membership_signup_source: 'notify_drawer',
            },
          }),
        });
      })
      .catch(function (err) {
        console.error('[kundeklub] drawer session attributes failed', err);
      });
  }

  /* Members get no boxes (snippets/spejder-notify-fields.liquid), so there is
     nothing to tick. A visitor who joined earlier in this visit has already
     given consent, and the cart carries it (membership_signup,
     membership_terms_accepted, membership_marketing_consent, written by
     whichever form they joined in). The typed address is enrolled with that
     consent: the customer is created or found, made a member, and subscribed
     only if they agreed to marketing (Magnus 2026-10-02). A member by account
     alone (logged in, no signup this visit) has no signup on the cart, so
     nothing is sent. As in joinKundeklub, the alert is already committed, so
     a failure here only logs. */
  function enrolSessionMember(email) {
    fetch('/cart.js')
      .then(function (res) {
        if (!res.ok) throw new Error('cart read failed (' + res.status + ')');
        return res.json();
      })
      .then(function (cart) {
        var attributes = (cart && cart.attributes) || {};
        if (attributes.membership_signup !== 'yes' || attributes.membership_terms_accepted !== 'yes') return;
        return fetch(MEMBERSHIP_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            termsAccepted: true,
            marketingConsent: attributes.membership_marketing_consent === 'yes',
            source: 'notify_drawer',
          }),
        }).then(function (res) {
          if (!res.ok) throw new Error('membership signup rejected (' + res.status + ')');
        });
      })
      .catch(function (err) {
        console.error('[kundeklub] drawer member enrolment failed', err);
      });
  }

  function subscribe(form) {
    var host = form.closest('[data-klaviyo-company-id]');
    var companyId = host ? host.getAttribute('data-klaviyo-company-id') : '';
    var emailEl = form.querySelector(SELECTORS.email);
    var email = emailEl ? emailEl.value.trim() : '';
    var variantId = catalogVariantId(form.getAttribute('data-variant-id'));

    var member = form.querySelector(SELECTORS.membership);
    var marketing = form.querySelector(SELECTORS.marketing);
    var joining = !!(member && member.checked);

    var invalidEmail = message(form, 'data-error-email', 'Der opstod en fejl — indtast venligst en gyldig e-mailadresse.');
    var genericError = message(form, 'data-error-generic', 'Vi kunne ikke oprette din besked lige nu. Prøv igen om et øjeblik.');
    var consentError = message(form, 'data-error-consent', 'Du skal acceptere markedsføring for at melde dig ind i kundeklubben.');

    // checkValidity() covers the type="email" rules; the form carries novalidate
    // so the browser's own bubble never competes with our in-drawer message.
    if (!email || (emailEl && !emailEl.checkValidity())) {
      showError(form, invalidEmail);
      if (emailEl) emailEl.focus();
      return;
    }

    // Joining the kundeklub requires marketing consent, exactly as in the
    // member modal. Not joining? Then the marketing box is disabled and
    // there is nothing to enforce.
    if (joining && marketing && !marketing.checked) {
      showError(form, consentError);
      marketing.focus();
      return;
    }

    /* No company id or no resolvable variant means the drawer cannot subscribe
       anyone. Fail loudly in the console (a misconfigured Site ID is a setup
       error, not a shopper error) but keep the shopper-facing copy generic. */
    if (!companyId || !variantId) {
      console.warn('[spejder-notify] missing Klaviyo company id or variant id', {
        companyId: companyId,
        variantId: form.getAttribute('data-variant-id'),
      });
      showError(form, genericError);
      return;
    }

    clearError(form);
    setBusy(form, true);

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: variantId } },
        },
      },
    };

    fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        // 202 Accepted is the documented success; treat any 2xx as success.
        if (res.ok) {
          showSuccess(form);
          if (joining) joinKundeklub(email, !!(marketing && marketing.checked));
          else if (!member) enrolSessionMember(email);
          return;
        }
        // 400 with an unknown catalog variant is the realistic failure here:
        // the product exists in Shopify but has not synced to Klaviyo's
        // catalog yet. Nothing the shopper can fix, so keep the copy generic
        // and put the detail where a developer will find it.
        return res
          .text()
          .catch(function () {
            return '';
          })
          .then(function (body) {
            console.warn('[spejder-notify] Klaviyo rejected the subscription', res.status, body);
            showError(form, genericError);
          });
      })
      .catch(function (err) {
        console.warn('[spejder-notify] Klaviyo request failed', err);
        showError(form, genericError);
      })
      .then(function () {
        setBusy(form, false);
      });
  }

  /* Marketing consent only exists as a decision if they are joining: disabled
     while the kundeklub box is unticked, required once it is ticked — the same
     rule the member modal applies (Magnus 2026-09-07). Enforced from JS rather
     than rendered disabled in Liquid so that without JS both boxes stay plain
     and tickable, which is exactly the inert fallback-mode behaviour. */
  function syncConsents(form) {
    var member = form.querySelector(SELECTORS.membership);
    var marketing = form.querySelector(SELECTORS.marketing);
    if (!member || !marketing) return;
    var joining = member.checked;
    marketing.disabled = !joining;
    marketing.required = joining;
    if (!joining) marketing.checked = false;
    var row = marketing.closest('label');
    if (row) row.classList.toggle('is-disabled', !joining);
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches(SELECTORS.membership)) return;
    var form = target.closest(SELECTORS.form);
    if (form) syncConsents(form);
  });

  /* ONE drawer instance serves every sold-out size on a product, so a success
     state left over from the previous size would greet the next one ("Tak!"
     with no form). Every surface opens the drawer by clicking the hidden
     internal proxy — the PDP with a real .click(), quick-add-product.js with a
     dispatched Event — so resetting on that click covers all four without
     either of those files needing to know about Klaviyo. Capture phase is
     required: quick-add's `new Event('click')` does not bubble, but the
     capture path from document to target always runs. */
  function resetForm(form) {
    var host = form.closest(SELECTORS.host);
    var requestEl = form.querySelector(SELECTORS.request);
    var successEl = form.querySelector(SELECTORS.success);
    if (host) host.classList.remove('has-success');
    if (requestEl) requestEl.classList.remove('hidden');
    if (successEl) successEl.hidden = true;
    clearError(form);
    setBusy(form, false);
    syncConsents(form);
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var trigger = target.closest(SELECTORS.internalTrigger);
      if (!trigger) return;
      var host = trigger.closest(SELECTORS.host);
      var form = host && host.querySelector(SELECTORS.form);
      if (form) resetForm(form);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches(SELECTORS.form)) return;
      event.preventDefault();
      subscribe(form);
    },
    true
  );
})();
